import { isDevMode } from "@/constants/env";
import CryptoJS from "crypto-js";

// ─────────────────────────────────────────────────────────────
// 환경 변수 — .env 참고
// REACT_APP_API_RESERVATION_TEST_URL / REACT_APP_API_RESERVATION_OFFICIAL_URL → 사전등록(api_copy.php)
// REACT_APP_API_ADMIN_TEST_URL       / REACT_APP_API_ADMIN_OFFICIAL_URL       → 관리자(api_admin.php)
// REACT_APP_API_KEY                                                           → HMAC 공통 비밀키
// ─────────────────────────────────────────────────────────────

// 운영/개발 환경 토글
// @/constants/env 의 isDevMode 만으로 API 주소를 스위칭한다.
// true  -> TEST URL
// false -> OFFICIAL URL
const getRuntimeMode = () => (isDevMode ? "TEST" : "OFFICIAL");

export const MODE = getRuntimeMode(); // "TEST" | "OFFICIAL"

const BASE_URLS = {
  reservation: {
    TEST: process.env.REACT_APP_API_RESERVATION_TEST_URL,
    OFFICIAL: process.env.REACT_APP_API_RESERVATION_OFFICIAL_URL,
  },
  admin: {
    TEST: process.env.REACT_APP_API_ADMIN_TEST_URL,
    OFFICIAL: process.env.REACT_APP_API_ADMIN_OFFICIAL_URL,
  },
};

const ENV_KEY_MAP = {
  reservation: {
    TEST: "REACT_APP_API_RESERVATION_TEST_URL",
    OFFICIAL: "REACT_APP_API_RESERVATION_OFFICIAL_URL",
  },
  admin: {
    TEST: "REACT_APP_API_ADMIN_TEST_URL",
    OFFICIAL: "REACT_APP_API_ADMIN_OFFICIAL_URL",
  },
};

const KEY = process.env.REACT_APP_API_KEY;

const getBaseUrl = (target) => BASE_URLS?.[target]?.[MODE] ?? null;
const getBaseEnvName = (target) => ENV_KEY_MAP?.[target]?.[MODE] ?? "";

export async function callApi({
  endpoint = "",
  method = "POST",
  body = null,
  target = "admin", // "reservation" | "admin"
}) {
  const BASE = getBaseUrl(target);

  if (!BASE) {
    return {
      error: "환경 변수 누락",
      message: getBaseEnvName(target) || `Unknown target: ${target}`,
    };
  }
  if (!KEY) {
    return { error: "환경 변수 누락", message: "REACT_APP_API_KEY" };
  }

  const headers = {
    "Content-Type": "application/json",
  };

  const payloadString = JSON.stringify(body);

  let hash;
  try {
    hash = CryptoJS.HmacSHA256(payloadString, KEY).toString(CryptoJS.enc.Hex);
  } catch (error) {
    return {
      error: "HMAC 생성 실패",
      message: String(error?.message || error),
    };
  }
  headers["X-Auth-Hash"] = hash;

  // PATH_INFO + ?mode= 동시 전달 (호스팅 호환)
  const modeName = String(endpoint).replace(/^\/+/, "");
  const url = modeName
    ? `${BASE}${endpoint}?mode=${encodeURIComponent(modeName)}`
    : BASE;

  try {
    const response = await fetch(url, {
      method,
      headers,
      body: payloadString,
    });

    const text = await response.text();
    try {
      return JSON.parse(text);
    } catch {
      return { message: text };
    }
  } catch (error) {
    return {
      error: "요청 중 오류 발생.",
      message: String(error?.message || error),
    };
  }
}

// ─────────────────────────────────────────────────────────────
// 기능별 사용 가이드
// ─────────────────────────────────────────────────────────────
// 현재 2026_git은 프론트 화면 프로젝트다.
// member_list, log_list, approval 같은 관리자 목록/로그/승인 기능은 여기서 사용하지 않는다.
//
// @/constants/env 의 isDevMode 값이 API URL 모드를 결정한다. true면 TEST, false면 OFFICIAL이다.
// target 기본값은 "admin"이다. 화면 콘텐츠 조회 API는 target을 생략해도 api_admin.php로 간다.
// api_copy.php를 호출해야 하는 기능만 target: "reservation"을 명시한다.
//
// 사용 예시
//   callApi({ endpoint: "/main_list", body: { year: 2026 } })
//   callApi({ endpoint: "/reservation", body, target: "reservation" })
//
// [target: "reservation"] api_copy.php
//   사전예약 등록: /reservation
//   - reservationApi.reservation에서만 사용한다.
//   - 서버 내부에서 신청 접수/완료/실패 로그(SEND_CHECKED, SEND_COMPLETED, SEND_FAILED)를 기록한다.
//
// [target: "admin"] api_admin.php
//   메인: /main_list
//   이벤트/전시: /event_list, /booth_list
//   트랙/세션: /track_list, /session_list
//   오시는 길: /location_list
//   이전 행사: /previous_list
//   공통 콘텐츠: /channel_list, /familysite_list, /about_list, /faq_list
//   행사 연도: /event_year_list
//
// api_admin.php에 create/update/delete/login/logout/upload/member_list case가 있더라도
// 현재 2026 프론트 화면의 실제 호출 대상은 위 조회 API로 제한한다.
// ─────────────────────────────────────────────────────────────
